/**
 * Shared client-side shift validation (DNKADM-0053) — the schedule shift rules
 * mirrored from the server (handoff §5.6, family of §5.2/§5.7), extracted so the
 * staff weekly schedule / date-exception editors here and the resource editors of
 * DNKADM-0054 validate IDENTICALLY. It carries NO entity knowledge: a caller
 * passes a `{ startTime, endTime }[]` and gets back a human message (or `null`).
 *
 * This is the same logic that `src/features/operating-hours/validateHours.ts`
 * (DNKADM-0051) implements for per-location hours; that module predates this
 * shared util and keeps its own structure (in-scope only — no opportunistic
 * migration). Since DNKADM-0318 both modules import the `"HH:mm"` → minute parsing
 * below so the end-of-day rule is defined once. New schedule features (0053 staff,
 * 0054 resources) share THIS one.
 *
 * **Shift rules (per available day / date / scope):**
 *  - every shift has a valid `"HH:mm"` `startTime`/`endTime`;
 *  - `start < end`, where an **end of `"00:00"` means end of day (minute 1440)**
 *    — mirrors dink-api's `ShiftBounds` (DNKAPI-0486): there is no `24:00` on the
 *    wire, `"00:00"` as an END is the representation of midnight, and it is valid
 *    for any start (`00:00–00:00` is a full day). A START of `"00:00"` keeps its
 *    ordinary meaning (minute 0). Every other `end <= start` is rejected as
 *    overnight (split across days);
 *  - no overlapping shifts within the same day/date (touching boundaries OK —
 *    adjacent shifts allowed, not auto-merged);
 *  - no exact duplicates;
 *  - an available day/date needs **≥1 shift** (else mark it unavailable/closed).
 */

/** One shift — an open interval `startTime`/`endTime` (`"HH:mm"`, 24h, local). */
export interface ShiftLike {
  startTime: string;
  endTime: string;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Minute-of-day an end of `"00:00"` denotes — end of day (24 × 60), per `ShiftBounds`. */
export const END_OF_DAY_MINUTES = 1440;

/** A valid `"HH:mm"` parses to its minute-of-day; an invalid one to null. */
function minutesOf(value: string): number | null {
  if (!HHMM.test(value)) return null;
  const [h, m] = value.split(':');
  return Number(h) * 60 + Number(m);
}

/** A shift START `"HH:mm"` → minute-of-day (`"00:00"` → 0); invalid → null. */
export function shiftStartMinutes(value: string): number | null {
  return minutesOf(value);
}

/**
 * A shift END `"HH:mm"` → minute-of-day, where `"00:00"` is end of day
 * ({@link END_OF_DAY_MINUTES}, 1440) so it sorts and overlap-tests AFTER every other
 * time of the same day; invalid → null.
 */
export function shiftEndMinutes(value: string): number | null {
  const minutes = minutesOf(value);
  return minutes === 0 ? END_OF_DAY_MINUTES : minutes;
}

/**
 * Validate a single day/date/scope's shift list against the shift rules. Returns a
 * human error message, or `null` when the list is valid. An empty list is invalid
 * (an available day/date needs ≥1 shift — the caller passes only available ones).
 */
export function validateShifts(shifts: ShiftLike[]): string | null {
  if (shifts.length === 0) {
    return 'Add at least one shift, or mark this unavailable.';
  }
  // Parse each shift to [start, end) minute ranges — an end of "00:00" is minute
  // 1440 (end of day) — then reject bad/overnight ranges.
  const ranges: Array<{ start: number; end: number }> = [];
  for (const shift of shifts) {
    const start = shiftStartMinutes(shift.startTime);
    const end = shiftEndMinutes(shift.endTime);
    if (start === null || end === null) {
      return 'Enter a valid start and end time (HH:mm) for every shift.';
    }
    if (start >= end) {
      return 'Each shift’s start must be before its end (overnight shifts aren’t allowed — an end of 12:00 AM means midnight).';
    }
    ranges.push({ start, end });
  }
  // Sort by start, then check for overlaps / duplicates. Touching boundaries
  // (prev.end === next.start) are adjacent and allowed. Ends are already
  // end-of-day-normalized, so an 18:00–00:00 shift sorts and sweeps as 18:00–24:00.
  const sorted = [...ranges].sort((a, b) => a.start - b.start || a.end - b.end);
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (prev === undefined || cur === undefined) continue;
    if (prev.start === cur.start && prev.end === cur.end) {
      return 'Two shifts are identical — remove the duplicate.';
    }
    if (cur.start < prev.end) {
      return 'Shifts can’t overlap within the same day.';
    }
  }
  return null;
}
